import { useEffect, useRef, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, useNavigate, Navigate } from "react-router-dom";
import LandingPage from "@/pages/LandingPage";
import SignupFlow from "@/pages/SignupFlow";
import GetStarted from "@/pages/GetStarted";
import AdminPage from "@/pages/AdminPage";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Dashboard from "@/pages/Dashboard";
import SitePreview from "@/pages/SitePreview";
import ExampleSite from "@/pages/ExampleSite";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import TermsOfService from "@/pages/TermsOfService";
import PublicSite from "@/pages/template/PublicSite";
import AdminLogin from "@/pages/template/AdminLogin";
import AdminVerify from "@/pages/template/AdminVerify";
import AdminLayout from "@/pages/template/AdminLayout";
import AdminDashboard from "@/pages/template/AdminDashboard";
import HeroEditor from "@/pages/template/HeroEditor";
import AboutEditor from "@/pages/template/AboutEditor";
import ContactEditor from "@/pages/template/ContactEditor";
import HoursEditor from "@/pages/template/HoursEditor";
import FooterEditor from "@/pages/template/FooterEditor";
import { getMe } from "@/lib/api";
import { exchangeSession } from "@/lib/api";

function AuthCallback() {
  const hasProcessed = useRef(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = window.location.hash;
    const params = new URLSearchParams(hash.replace('#', ''));
    const sessionId = params.get('session_id');

    if (!sessionId) {
      navigate('/login');
      return;
    }

    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    exchangeSession(sessionId)
      .then((res) => {
        navigate('/dashboard', { state: { user: res.data } });
      })
      .catch(() => {
        navigate('/login');
      });
  }, [navigate]);

  return (
    <div className="min-h-screen bg-[#0F172A] flex items-center justify-center">
      <div className="text-slate-400">Signing you in...</div>
    </div>
  );
}

function ProtectedRoute({ children }) {
  const [authState, setAuthState] = useState(null); // null = checking
  const [user, setUser] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (location.state?.user) {
      setUser(location.state.user);
      setAuthState(true);
      return;
    }
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    if (window.location.hash?.includes('session_id=')) {
      setAuthState(false);
      return;
    }
    getMe()
      .then((res) => {
        setUser(res.data);
        setAuthState(true);
      })
      .catch(() => {
        setAuthState(false);
        navigate('/login');
      });
  }, [location.state, navigate]);

  if (authState === null) {
    return (
      <div className="min-h-screen bg-[#0F172A] flex items-center justify-center">
        <div className="text-slate-400">Loading...</div>
      </div>
    );
  }
  if (authState === false) return null;
  return children;
}

function AppRouter() {
  const location = useLocation();

  // Synchronous check: detect session_id before Routes render
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/get-started" element={<GetStarted />} />
      <Route path="/signup" element={<Navigate to="/get-started" />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/admin" element={<ProtectedRoute><AdminPage /></ProtectedRoute>} />
      <Route path="/preview/:siteId" element={<SitePreview />} />
      <Route path="/examples/:slug" element={<ExampleSite />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/terms" element={<TermsOfService />} />
      {/* Template system routes */}
      <Route path="/t/:slug" element={<PublicSite />} />
      <Route path="/t/:slug/admin/login" element={<AdminLogin />} />
      <Route path="/t/:slug/admin/verify" element={<AdminVerify />} />
      <Route path="/t/:slug/admin" element={<AdminLayout />}>
        <Route index element={<AdminDashboard />} />
        <Route path="hero" element={<HeroEditor />} />
        <Route path="about" element={<AboutEditor />} />
        <Route path="contact" element={<ContactEditor />} />
        <Route path="hours" element={<HoursEditor />} />
        <Route path="footer" element={<FooterEditor />} />
      </Route>
      <Route path="*" element={<Navigate to="/" />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AppRouter />
    </BrowserRouter>
  );
}

export default App;
